import { AuthContext } from '../../context/AuthContext';
import { useLang } from '../../context/LangContext';
import { useEffect, useContext, useState } from 'react';
import { Navigate } from 'react-router-dom';
import esTexts from '../../translations/es.json';
import enTexts from '../../translations/en.json';
import axios from 'axios';
import Welcome from '../../components/Header/Welcome';
import Select from 'react-select';
import { AdminNav } from '../../components';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const { token, currentUser } = useContext(AuthContext);
  const { lang } = useLang();
  const texts = lang === 'es' ? esTexts : enTexts;

  const [selectedId, setSelectedId] = useState('');
  const [selectedEmail, setSelectedEmail] = useState('');
  const [selectedFullname, setSelectedFullname] = useState('');
  const [selectedPhone, setSelectedPhone] = useState('');
  const [selectedUniversityId, setSelectedUniversityId] = useState('');
  const [selectedPassword, setSelectedPassword] = useState('');
  const [selectedIsAdmin, setSelectedIsAdmin] = useState(false);

  const [saveButton, setSaveButton] = useState(true);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('success');

  const showFeedback = (message, type = 'success') => {
    setFeedbackMessage(message);
    setFeedbackType(type);
    setTimeout(() => {
      setFeedbackMessage('');
    }, 4000);
  };

  const getUsers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/${lang}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error(error);
    }
  };

  const getAllUniversities = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/universities`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const arrayObj = response.data.universities.map(item => ({
        value: item.id,
        label: item.name,
      }));

      setUniversities(arrayObj);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveUser = async () => {
    if (selectedEmail.trim() === '' || selectedPassword.trim() === '') {
      showFeedback('Email y contraseña son requeridos', 'danger');
      return;
    }

    try {
      const payload = {
        email: selectedEmail,
        fullname: selectedFullname,
        phone: selectedPhone,
        university_id: selectedIsAdmin ? null : selectedUniversityId?.value,
        password: selectedPassword,
        is_admin: selectedIsAdmin,
      };

      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/${lang}/users`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showFeedback('Usuario creado exitosamente');
      resetForm();
      getUsers();
    } catch (error) {
      showFeedback(error.response?.data?.message || 'Error al guardar usuario', 'danger');
    }
  };

  const handleEditUser = user => {
    setSelectedId(user.id);
    setSelectedEmail(user.email);
    setSelectedFullname(user.fullname || '');
    setSelectedPhone(user.phone || '');
    setSelectedIsAdmin(Boolean(user.is_admin));
    setSelectedPassword('');

    if (user.university_id) {
      const foundUni = universities.find(u => u.value === user.university_id);
      setSelectedUniversityId(foundUni || null);
    } else {
      setSelectedUniversityId(null);
    }

    setSaveButton(false);
  };

  const handleUpdateUser = async () => {
    try {
      const payload = {
        email: selectedEmail,
        fullname: selectedFullname,
        phone: selectedPhone,
        university_id: selectedIsAdmin ? null : selectedUniversityId?.value,
        is_admin: selectedIsAdmin,
      };

      if (selectedPassword.trim() !== '') {
        payload.password = selectedPassword;
      }

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/users/${selectedId}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      showFeedback('Usuario actualizado exitosamente');
      resetForm();
      getUsers();
    } catch (error) {
      showFeedback(error.response?.data?.message || 'Error al actualizar usuario', 'danger');
    }
  };

  const handleDeleteUser = async id => {
    const confirm = window.confirm(texts.admin_common.delete_confirm);
    if (!confirm) return;

    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/${lang}/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showFeedback('Usuario eliminado');
      getUsers();
    } catch (error) {
      showFeedback('Error al eliminar usuario', 'danger');
    }
  };

  const resetForm = () => {
    setSelectedId('');
    setSelectedEmail('');
    setSelectedFullname('');
    setSelectedPhone('');
    setSelectedUniversityId(null);
    setSelectedPassword('');
    setSelectedIsAdmin(false);
    setSaveButton(true);
  };

  useEffect(() => {
    if (token) {
      const fetchData = async () => {
        setIsLoading(true);
        await Promise.all([getUsers(), getAllUniversities()]);
        setIsLoading(false);
      };
      fetchData();
    }
  }, [token]);

  if (!currentUser?.isAdmin) {
    return <Navigate to="/admin" />;
  }

  return (
    <>
      <Welcome />

      <div className="container py-4" style={{ minHeight: '80vh' }}>
        <AdminNav />

        <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 pb-2 border-bottom">
          <div>
            <h2 className="fw-bold text-sen mb-1 d-flex align-items-center gap-2">
              <i className="fa fa-users"></i>
              <span>
                {texts.admin_common.list_of} {texts.admin.menu_users}
              </span>
            </h2>
            <span className="text-muted small">
              {lang === 'es'
                ? 'Administra las cuentas de usuario, roles de acceso y contraseñas de la plataforma.'
                : 'Manage user accounts, platform access roles, and credentials.'}
            </span>
          </div>
        </div>

        {feedbackMessage && (
          <div
            className={`alert alert-${feedbackType} alert-dismissible fade show rounded-3 shadow-sm mb-4`}
            role="alert"
          >
            <i
              className={`fa ${feedbackType === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}
            ></i>
            <strong>{feedbackMessage}</strong>
          </div>
        )}

        <div className="row g-4">
          {/* Form Card */}
          <div className="col-12 col-lg-4">
            <div
              className="card border-0 shadow-sm rounded-4 bg-white sticky-top"
              style={{ top: '20px' }}
            >
              <div className="card-body p-4">
                <h5 className="fw-bold text-sen mb-3 d-flex align-items-center gap-2">
                  <i className={saveButton ? 'fa fa-user-plus' : 'fa fa-user-circle'}></i>
                  <span>{saveButton ? 'Nuevo Usuario' : 'Editar Usuario'}</span>
                </h5>

                <div className="mb-3">
                  <label className="form-label small fw-bold text-secondary">
                    Correo Electrónico *
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0 text-muted">
                      <i className="fa fa-envelope"></i>
                    </span>
                    <input
                      type="email"
                      className="form-control bg-light border-start-0"
                      placeholder="ejemplo@universidad.cl"
                      value={selectedEmail}
                      onChange={e => setSelectedEmail(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold text-secondary">
                    {texts.admin_common.fullname}
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0 text-muted">
                      <i className="fa fa-user"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control bg-light border-start-0"
                      placeholder="Nombre Completo"
                      value={selectedFullname}
                      onChange={e => setSelectedFullname(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold text-secondary">
                    {texts.admin_common.phone}
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0 text-muted">
                      <i className="fa fa-phone"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control bg-light border-start-0"
                      placeholder="+56 9 1234 5678"
                      value={selectedPhone}
                      onChange={e => setSelectedPhone(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold text-secondary">Rol de Acceso</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0 text-muted">
                      <i className="fa fa-shield"></i>
                    </span>
                    <select
                      className="form-select bg-light border-start-0"
                      value={selectedIsAdmin ? 'admin' : 'user'}
                      onChange={e => setSelectedIsAdmin(e.target.value === 'admin')}
                    >
                      <option value="user">Representante Universitario</option>
                      <option value="admin">Administrador del Sistema</option>
                    </select>
                  </div>
                </div>

                {!selectedIsAdmin && (
                  <div className="mb-3">
                    <label className="form-label small fw-bold text-secondary">
                      {texts.admin.menu_universities}
                    </label>
                    <Select
                      options={universities}
                      onChange={opt => setSelectedUniversityId(opt)}
                      value={selectedUniversityId}
                      placeholder="Seleccionar Universidad..."
                      isClearable
                    />
                  </div>
                )}

                <div className="mb-4">
                  <label className="form-label small fw-bold text-secondary">
                    {texts.admin_common.password}
                    {!saveButton && (
                      <small className="text-muted fw-normal ms-1">
                        (opcional para actualizar)
                      </small>
                    )}
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0 text-muted">
                      <i className="fa fa-key"></i>
                    </span>
                    <input
                      type="password"
                      className="form-control bg-light border-start-0"
                      placeholder={
                        saveButton ? 'Contraseña inicial' : 'Nueva contraseña (opcional)'
                      }
                      value={selectedPassword}
                      onChange={e => setSelectedPassword(e.target.value)}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 pt-2 border-top">
                  {!saveButton && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-3"
                      onClick={resetForm}
                    >
                      {texts.admin_common.cancel}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary bg-sen border-0 px-4 fw-bold shadow-sm"
                    onClick={saveButton ? handleSaveUser : handleUpdateUser}
                  >
                    {saveButton ? texts.admin_common.save : texts.admin_common.update}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div className="col-12 col-lg-8">
            <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="py-3 px-4 text-secondary small fw-bold text-uppercase">
                          Usuario
                        </th>
                        <th className="py-3 px-3 text-secondary small fw-bold text-uppercase">
                          Rol / Universidad
                        </th>
                        <th className="py-3 px-3 text-secondary small fw-bold text-uppercase">
                          Teléfono
                        </th>
                        <th className="py-3 px-4 text-secondary small fw-bold text-uppercase text-end">
                          {texts.admin_common.options}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan="4" className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Cargando...</span>
                            </div>
                          </td>
                        </tr>
                      ) : users.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center py-5 text-muted">
                            <i className="fa fa-users fs-3 d-block mb-2 text-secondary"></i>
                            No hay usuarios registrados
                          </td>
                        </tr>
                      ) : (
                        users.map(user => (
                          <tr key={user.id}>
                            <td className="py-3 px-4">
                              <div className="fw-semibold text-dark">
                                {user.fullname || 'Sin nombre'}
                              </div>
                              <div className="small text-muted">{user.email}</div>
                            </td>
                            <td className="py-3 px-3">
                              <div className="d-flex flex-column gap-1">
                                <span
                                  className={`badge ${user.is_admin ? 'bg-primary' : 'bg-secondary'} w-auto align-self-start`}
                                >
                                  <i
                                    className={`fa ${user.is_admin ? 'fa-shield' : 'fa-user'} me-1`}
                                  ></i>
                                  {user.is_admin ? 'Administrador' : 'Universidad'}
                                </span>
                                {user.university?.name && (
                                  <span className="badge bg-light text-dark border w-auto align-self-start">
                                    <i className="fa fa-university me-1"></i>
                                    {user.university.name}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-3 small text-muted">
                              {user.phone ? (
                                <span>
                                  <i className="fa fa-phone me-1"></i>
                                  {user.phone}
                                </span>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="py-3 px-4 text-end">
                              <button
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={() => handleEditUser(user)}
                                title="Editar"
                              >
                                <i className="fa fa-edit"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteUser(user.id)}
                                title="Eliminar"
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Users;
